/* ════════════ THE RELAY MESH ════════════
   The nostr page's atmosphere: relays drift as glowing squares, faint
   lines bind the neighbors, and signed notes hop from relay to relay
   as bright little pulses. Dumb pipes, visualized. */

import { onPage } from "./router";

const REDUCED = matchMedia("(prefers-reduced-motion: reduce)").matches;

function mesh(signal: AbortSignal) {
  const c = document.querySelector<HTMLCanvasElement>("#mesh")!;
  const ctx = c.getContext("2d")!;

  const PURPLE = "#a06bff";
  const GREEN = "#00ffa3";
  const LINK_DIST = 240;

  type Relay = { x: number; y: number; vx: number; vy: number; s: number };
  type Note = { from: number; to: number; t: number; v: number };
  let relays: Relay[] = [];
  let notes: Note[] = [];

  const seed = () => {
    c.width = innerWidth;
    c.height = innerHeight;
    const count = Math.min(26, Math.floor((c.width * c.height) / 60000));
    relays = Array.from({ length: count }, () => ({
      x: Math.random() * c.width,
      y: Math.random() * c.height,
      vx: (Math.random() - 0.5) * 0.18,
      vy: (Math.random() - 0.5) * 0.18,
      s: Math.random() > 0.75 ? 4 : 3,
    }));
    notes = [];
  };
  seed();
  addEventListener("resize", seed, { signal });

  const dist = (a: Relay, b: Relay) => Math.hypot(a.x - b.x, a.y - b.y);

  const spawnNote = () => {
    if (relays.length < 2 || notes.length > 14) return;
    const from = (Math.random() * relays.length) | 0;
    // prefer a reachable neighbor so pulses travel the drawn lines
    const near = relays
      .map((r, i) => ({ i, d: dist(relays[from]!, r) }))
      .filter((n) => n.i !== from && n.d < LINK_DIST * 1.4);
    if (!near.length) return;
    const to = near[(Math.random() * near.length) | 0]!.i;
    notes.push({ from, to, t: 0, v: 0.008 + Math.random() * 0.012 });
  };

  const draw = () => {
    ctx.clearRect(0, 0, c.width, c.height);
    // links
    for (let i = 0; i < relays.length; i++)
      for (let j = i + 1; j < relays.length; j++) {
        const a = relays[i]!;
        const b = relays[j]!;
        const d = dist(a, b);
        if (d > LINK_DIST) continue;
        ctx.globalAlpha = (1 - d / LINK_DIST) * 0.16;
        ctx.strokeStyle = PURPLE;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }
    // relays
    for (const r of relays) {
      ctx.globalAlpha = 0.7;
      ctx.fillStyle = PURPLE;
      ctx.fillRect((r.x - r.s / 2) | 0, (r.y - r.s / 2) | 0, r.s, r.s);
    }
    // notes in flight
    for (const n of notes) {
      const a = relays[n.from]!;
      const b = relays[n.to]!;
      const x = a.x + (b.x - a.x) * n.t;
      const y = a.y + (b.y - a.y) * n.t;
      ctx.globalAlpha = 0.9;
      ctx.fillStyle = GREEN;
      ctx.fillRect((x - 1) | 0, (y - 1) | 0, 2, 2);
    }
    ctx.globalAlpha = 1;
  };

  if (REDUCED) {
    draw();
    return;
  }
  const tick = () => {
    if (signal.aborted) return;
    for (const r of relays) {
      r.x += r.vx;
      r.y += r.vy;
      if (r.x < 0 || r.x > c.width) r.vx *= -1;
      if (r.y < 0 || r.y > c.height) r.vy *= -1;
    }
    for (const n of notes) n.t += n.v;
    notes = notes.filter((n) => n.t < 1);
    if (Math.random() < 0.08) spawnNote();
    draw();
    requestAnimationFrame(tick);
  };
  tick();
}

onPage(mesh);
